import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ShepherdService } from 'angular-shepherd';
import { filter, Subject, takeUntil } from 'rxjs';
import { tourExitButton, tourNextButton } from '../../data/tour-buttons';
import { DEFAULT_STEP_OPTIONS, getCurrentTourVersion, TourStepDefinition, TourType } from '../../data/tour-config';
import { TourProgressStatus } from '../../models/tour-progress';
import { upsertTourProgress } from '../../store/actions/tour-progress.actions';
import { getAppointmentsTour } from '../../tours/appointments-tour';
import { getHomeTour } from '../../tours/home-tour';
import { getMedicalRecordTour } from '../../tours/medical-record-tour';
import { getMedicationsTour } from '../../tours/medications-tour';
import { getMessagesTour } from '../../tours/messages-tour';
import { getResultsTour } from '../../tours/results-tour';

@Injectable({
  providedIn: 'root',
})
export class TourService implements OnDestroy {
  private shepherdService = inject(ShepherdService);
  private store = inject(Store);
  private ngZone = inject(NgZone);
  private router = inject(Router);

  private currentTourType: TourType | null = null;
  private activeTourRoute: string | null = null;
  /** Which status the in-flight exit should be recorded as; see {@link finish}. */
  private pendingStatus: TourProgressStatus | null = null;
  private pendingPersist = true;
  private removeWheelListener?: () => void;
  private removeTouchListener?: () => void;
  private resizeTimeout?: ReturnType<typeof setTimeout>;
  private destroy$ = new Subject<void>();
  private currentVersions = new Map<TourType, number>();

  constructor() {
    this.shepherdService.defaultStepOptions = DEFAULT_STEP_OPTIONS;
    this.shepherdService.modal = true;

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(event => {
        const currentUrl = this.routePath(event.urlAfterRedirects);

        if (this.activeTourRoute && !currentUrl.startsWith(this.activeTourRoute)) {
          this.cancel();
        }
      });
  }

  /**
   * Start a tour. For a brand-new user (seenVersion 0) the full tour is shown.
   * For a returning user only the steps added since their seenVersion are shown,
   * prefixed with a "What's new" intro.
   */
  start(tourType: TourType, seenVersion: number = 0): void {
    if (this.currentTourType) {
      return;
    }

    const allSteps = this.getTourByType(tourType);

    let steps: TourStepDefinition[];
    if (seenVersion > 0) {
      steps = this.getWhatsNewSteps(allSteps, seenVersion);
      if (steps.length === 0) {
        return; // nothing new since the user last saw this tour
      }
    } else {
      steps = allSteps;
    }

    this.beginTour(tourType, steps);
  }

  /** The current version of a tour, derived from its step definitions. */
  getCurrentVersion(tourType: TourType): number {
    const cached = this.currentVersions.get(tourType);
    if (cached) {
      return cached;
    }

    const currentVersion = getCurrentTourVersion(this.getTourByType(tourType));
    this.currentVersions.set(tourType, currentVersion);
    return currentVersion;
  }

  complete(): void {
    this.finish(TourProgressStatus.Completed);
  }

  skip(): void {
    this.finish(TourProgressStatus.Skipped);
  }

  restart(): void {
    if (!this.currentTourType) {
      return;
    }

    const tourType = this.currentTourType;

    this.finish(TourProgressStatus.Completed, false);
    // Replay the full tour without the welcome/intro step.
    this.beginTour(tourType, this.getTourByType(tourType).slice(1));
  }

  private beginTour(tourType: TourType, steps: TourStepDefinition[]): void {
    this.currentTourType = tourType;
    this.activeTourRoute = this.routePath(this.router.url);

    // Strip our versioning metadata before handing steps to Shepherd.
    this.shepherdService.addSteps(
      this.withRunStepCounters(steps).map(step => {
        const shepherdStep = { ...step };
        delete shepherdStep.addedInVersion;
        return shepherdStep;
      })
    );

    // addSteps builds a fresh Shepherd.Tour, so these listeners belong to this run only and cannot
    // accumulate across tours. Both terminal events must be handled: Shepherd reaches 'complete' on
    // its own whenever the last step's Next button is pressed, which is the normal ending for a
    // "What's new" run (those steps carry the standard Next, not our custom finish button).
    const tour = this.shepherdService.tourObject;
    tour?.on('complete', this.onShepherdComplete);
    tour?.on('cancel', this.onShepherdCancel);

    this.disableScrolling();
    this.initializeResizeHandler();
    this.shepherdService.start();
  }

  private getWhatsNewSteps(allSteps: TourStepDefinition[], seenVersion: number): TourStepDefinition[] {
    const newSteps = allSteps.filter(step => (step.addedInVersion ?? 1) > seenVersion);
    if (newSteps.length === 0) {
      return [];
    }
    return [this.getWhatsNewIntroStep(), ...newSteps];
  }

  private getWhatsNewIntroStep(): TourStepDefinition {
    return {
      id: 'whatsNewIntro',
      title: "What's New",
      text: "We've added new features since your last visit. Here's a quick look at what's changed.",
      buttons: [tourExitButton(this, 'Skip'), tourNextButton('Show Me')],
    };
  }

  /**
   * The only way a tour ends from our side. Records which status the exit should be attributed to,
   * then asks Shepherd to close; Shepherd's own terminal event calls {@link onTourEnded}, which does
   * the persisting and teardown. Keeping a single funnel means a user-driven exit and a
   * Shepherd-driven one (Escape, overlay click, last-step Next) converge on one code path.
   */
  private finish(status: TourProgressStatus, persist = true): void {
    if (!this.currentTourType) {
      return;
    }

    this.pendingStatus = status;
    this.pendingPersist = persist;

    const tour = this.shepherdService.tourObject;
    if (tour?.isActive()) {
      // Fires 'cancel'/'complete' synchronously, which lands in onTourEnded.
      if (status === TourProgressStatus.Dismissed) {
        tour.cancel();
      } else {
        tour.complete();
      }
      return;
    }

    // No live Shepherd tour to raise the event (already torn down, or never started).
    this.onTourEnded(status);
  }

  /**
   * Single teardown path, reached from Shepherd's 'complete'/'cancel' events.
   *
   * Order matters: currentTourType is cleared BEFORE anything else. Shepherd triggers the terminal
   * event from inside Tour._done while Shepherd.activeTour is still set, so isActive() is still true
   * at this point. Persisting or calling back into Shepherd first would re-enter this path and
   * recurse until the stack blew, dispatching an upsert on every frame.
   */
  private onTourEnded = (fallbackStatus: TourProgressStatus): void => {
    const tourType = this.currentTourType;
    if (!tourType) {
      return;
    }

    this.currentTourType = null;
    this.activeTourRoute = null;

    const status = this.pendingStatus ?? fallbackStatus;
    const persist = this.pendingPersist;
    this.pendingStatus = null;
    this.pendingPersist = true;

    if (persist) {
      this.store.dispatch(
        upsertTourProgress({
          tourType,
          seenVersion: this.getCurrentVersion(tourType),
          status,
        })
      );
    }

    this.enableScrolling();
    this.destroyResizeHandler();
    document.body.classList.remove('shepherd-resizing');
  };

  private onShepherdCancel = () => {
    this.ngZone.run(() => this.onTourEnded(TourProgressStatus.Dismissed));
  };

  private onShepherdComplete = () => {
    this.ngZone.run(() => this.onTourEnded(TourProgressStatus.Completed));
  };

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.enableScrolling();
    this.destroyResizeHandler();
    document.body.classList.remove('shepherd-resizing');
  }

  private getTourByType(tourType: TourType): TourStepDefinition[] {
    switch (tourType) {
      case TourType.Home:
        return getHomeTour(this);
      case TourType.Medications:
        return getMedicationsTour(this);
      case TourType.Messages:
        return getMessagesTour(this);
      case TourType.Results:
        return getResultsTour(this);
      case TourType.Appointments:
        return getAppointmentsTour(this);
      case TourType.MedicalRecord:
        return getMedicalRecordTour(this);
    }
  }

  // Dismissal path (navigating away from the tour's route). Escape and overlay clicks arrive via
  // Shepherd's own 'cancel' event instead, and both land in onTourEnded.
  private cancel = () => {
    this.finish(TourProgressStatus.Dismissed);
  };

  private disableScrolling() {
    this.disableMouseWheel();
    this.disableTouchScroll();
  }

  private enableScrolling() {
    this.enableMouseWheel();
    this.enableTouchScroll();
  }

  private disableMouseWheel() {
    if (this.removeWheelListener) return;

    this.ngZone.runOutsideAngular(() => {
      const handler = (event: WheelEvent) => {
        event.preventDefault();
      };

      window.addEventListener('wheel', handler, {
        passive: false,
        capture: true,
      });

      this.removeWheelListener = () => {
        window.removeEventListener('wheel', handler, {
          capture: true,
        } as AddEventListenerOptions);

        this.removeWheelListener = undefined;
      };
    });
  }

  private disableTouchScroll() {
    if (this.removeTouchListener) return;

    this.ngZone.runOutsideAngular(() => {
      const handler = (event: TouchEvent) => {
        event.preventDefault();
      };

      window.addEventListener('touchmove', handler, {
        passive: false,
        capture: true,
      });

      this.removeTouchListener = () => {
        window.removeEventListener('touchmove', handler, {
          capture: true,
        } as AddEventListenerOptions);

        this.removeTouchListener = undefined;
      };
    });
  }

  private enableMouseWheel() {
    this.removeWheelListener?.();
  }

  private enableTouchScroll() {
    this.removeTouchListener?.();
  }

  private initializeResizeHandler() {
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('resize', this.onResize);
    });
  }

  private destroyResizeHandler() {
    window.removeEventListener('resize', this.onResize);
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = undefined;
    document.body.classList.remove('shepherd-resizing');
  }

  private onResize = () => {
    const tour = this.shepherdService.tourObject;
    const currentStep = tour?.getCurrentStep();

    if (!tour || !currentStep) return;

    const currentStepId = currentStep.id;

    document.body.classList.add('shepherd-resizing');

    clearTimeout(this.resizeTimeout);

    this.resizeTimeout = setTimeout(() => {
      this.ngZone.run(() => {
        requestAnimationFrame(() => {
          tour.show(currentStepId);

          requestAnimationFrame(() => {
            document.body.classList.remove('shepherd-resizing');
          });
        });
      });
    }, 150);
  };

  private routePath(url: string): string {
    return url.split(/[?#]/)[0];
  }

  private withRunStepCounters(steps: TourStepDefinition[]): TourStepDefinition[] {
    const total = steps.filter(step => step.buttons?.some(button => this.isCountedNextButton(button))).length;
    let current = 0;

    return steps.map(step => {
      const buttons = step.buttons?.map(button => {
        if (!this.isCountedNextButton(button)) {
          return button;
        }

        current += 1;
        const text = `${current === total ? 'Done' : 'Next'} (${current} of ${total})`;
        return { ...button, text, label: text };
      });

      return buttons ? { ...step, buttons } : step;
    });
  }

  private isCountedNextButton(button: NonNullable<TourStepDefinition['buttons']>[number]): boolean {
    const buttonType = 'type' in button ? button.type : undefined;
    if (buttonType !== 'next') {
      return false;
    }

    const text = String(button.label ?? button.text ?? '');
    return /^(Next|Done) \(\d+ of \d+\)$/.test(text);
  }
}
